import React, { useState } from 'react';
import './CaptureDisposition.css';

interface FormData {
  captureStatus: string;
  applicationNumber: string;
  reasonsForLoss: string[];
  competitor: string;
}

const CaptureDisposition: React.FC<{ user: any; signOut: ((data?: any) => void) | undefined }> = ({ user, signOut }) => {
  const [formData, setFormData] = useState<FormData>({
    captureStatus: '',
    applicationNumber: '',
    reasonsForLoss: [],
    competitor: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<{
    type: 'success' | 'error' | '';
    message: string;
  }>({ type: '', message: '' });

  // Get the logged-in username from Cognito
  const username = user?.username || user?.attributes?.preferred_username || 'user';

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: 'reasonsForLoss'
  ) => {
    const { value, checked } = e.target;
    
    setFormData((prev) => {
      const currentArray = prev[fieldName];
      
      if (checked) {
        // Add value if checked and limit to 3 for reasonsForLoss
        if (currentArray.length >= 3) {
          return prev; // Don't add if already 3 selected
        }
        return {
          ...prev,
          [fieldName]: [...currentArray, value],
        };
      } else {
        // Remove value if unchecked
        return {
          ...prev,
          [fieldName]: currentArray.filter((item: string) => item !== value),
        };
      }
    });
  };

  const validateForm = (): boolean => {
    if (!formData.captureStatus) {
      setFeedbackMessage({
        type: 'error',
        message: 'Please select a capture status',
      });
      return false;
    }

    if (!formData.applicationNumber) {
      setFeedbackMessage({
        type: 'error',
        message: 'Please enter an application number',
      });
      return false;
    }

    const appNumber = parseInt(formData.applicationNumber, 10);
    if (isNaN(appNumber) || appNumber <= 0) {
      setFeedbackMessage({
        type: 'error',
        message: 'Application number must be a valid positive integer',
      });
      return false;
    }

    // If capture status is "No", validate additional fields (optional)
    if (formData.captureStatus === 'No') {
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedbackMessage({ type: '', message: '' });

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const apiEndpoint = 'https://07fmiu9hbe.execute-api.us-east-1.amazonaws.com/prod/sales';
      const apiKey = 'tE9vBATWsL25GX30mwKV94hQ9dCdesQC7ZP2iif7';

      // Build the JSON payload
      const payload = {
        captureStatus: formData.captureStatus,
        applicationNumber: formData.applicationNumber,
        reasonsForLoss: formData.reasonsForLoss,
        competitor: formData.competitor,
        username: username,
        userRole: 'Sales',
        timestamp: new Date().toISOString(),
      };

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const responseData = await response.json();
        
        // Check for DEFI error indicating invalid application number
        const defiIntegration = responseData.integrations?.defi;
        if (defiIntegration?.success === false) {
          const errorMsg = defiIntegration.error || defiIntegration.response;
          if (typeof errorMsg === 'string' && errorMsg.toLowerCase().includes('application number could not be found')) {
            setFeedbackMessage({
              type: 'error',
              message: 'Invalid Application Number. Please verify the application number and try again.',
            });
            setIsSubmitting(false);
            return;
          }
        }
        
        // Check for other integration errors
        const integrations = responseData.integrations || {};
        const hasErrors = Object.values(integrations).some(
          (integration: any) => integration.success === false
        );

        if (hasErrors) {
          const errorDetails = Object.entries(integrations)
            .filter(([_, integration]: [string, any]) => integration.success === false)
            .map(([name, integration]: [string, any]) => {
              const integrationName = name === 'defi' ? 'DEFI' : 'Data Ingestion';
              const errorMessage = integration.error || integration.response || integration.status;
              return `${integrationName}: ${errorMessage}`;
            })
            .join('. ');

          setFeedbackMessage({
            type: 'error',
            message: `${errorDetails}`,
          });
        } else {
          setFeedbackMessage({
            type: 'success',
            message: 'Disposition captured successfully!',
          });
        }
        
        // Reset form after successful submission
        setFormData({
          captureStatus: '',
          applicationNumber: '',
          reasonsForLoss: [],
          competitor: '',
        });
      } else {
        const errorData = await response.text();
        setFeedbackMessage({
          type: 'error',
          message: `Failed to capture disposition. Server responded with status: ${response.status}`,
        });
        console.error('API Error:', errorData);
      }
    } catch (error) {
      setFeedbackMessage({
        type: 'error',
        message: `Error submitting disposition: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="capture-disposition-container">
      <div className="capture-disposition-header">
        <img src="/flagship_logo.png" alt="Flagship Logo" />
        <div className="header-user-section">
          <span className="username-display">Welcome, {username}</span>
          <button onClick={() => signOut?.()} className="sign-out-button">
            Sign Out
          </button>
        </div>
      </div>
      <div className="capture-disposition-card">
        <h1>Capture Disposition</h1>

        <form onSubmit={handleSubmit} className="disposition-form">
          <div className="form-group">
            <label htmlFor="captureStatus">
              Capture Status <span className="required">*</span>
            </label>
            <select
              id="captureStatus"
              name="captureStatus"
              value={formData.captureStatus}
              onChange={handleInputChange}
              required
              disabled={isSubmitting}
            >
              <option value="">Select Status</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Maybe">Maybe</option>
              <option value="Left Message/No Contact">Left Message/No Contact</option>
              <option value="No Sale">No Sale</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="applicationNumber">
              Application Number <span className="required">*</span>
            </label>
            <input
              type="number"
              id="applicationNumber"
              name="applicationNumber"
              value={formData.applicationNumber}
              onChange={handleInputChange}
              placeholder="Enter application number"
              required
              disabled={isSubmitting}
              min="1"
            />
          </div>

          {formData.captureStatus === 'No' && (
            <>
              <div className="form-group checkbox-group">
                <label className="group-label">
                  Reasons for Loss
                  <span className="helper-text">(Select up to 3)</span>
                </label>
                <div className="checkbox-container">
                  {[
                    'Advance',
                    'Advance ($0-$999)',
                    'Advance ($1000-$2000)',
                    'Advance ($2000+)',
                    'Backend',
                    'Down Payment',
                    'Fee',
                    'Fee ($0 - $499)',
                    'Fee ($1000+)',
                    'Fee ($500 - $999)',
                    'N/A',
                    'No Drivers License',
                    'No Stips',
                    'Other',
                    'Participation',
                    'Rate',
                    'Rate (0.00% - 0.99%)',
                    'Rate (1.00% - 1.99%)',
                    'Rate (2.00%+)',
                    'Stip to Trade',
                    'Term',
                  ].map((reason) => (
                    <label key={reason} className="checkbox-label">
                      <input
                        type="checkbox"
                        value={reason}
                        checked={formData.reasonsForLoss.includes(reason)}
                        onChange={(e) => handleCheckboxChange(e, 'reasonsForLoss')}
                        disabled={isSubmitting || (formData.reasonsForLoss.length >= 3 && !formData.reasonsForLoss.includes(reason))}
                      />
                      <span>{reason}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="competitor">
                  Competitor
                </label>
                <select
                  id="competitor"
                  name="competitor"
                  value={formData.competitor}
                  onChange={handleInputChange}

                  disabled={isSubmitting}
                >
                  <option value="">Select Competitor</option>
                  <option value="Ally">Ally</option>
                  <option value="American Credit Acceptance">American Credit Acceptance</option>
                  <option value="AmeriCredit">AmeriCredit</option>
                  <option value="Cap One">Cap One</option>
                  <option value="Captive Lender">Captive Lender</option>
                  <option value="Chase">Chase</option>
                  <option value="CIG">CIG</option>
                  <option value="CPS">CPS</option>
                  <option value="Credit Acceptance">Credit Acceptance</option>
                  <option value="Credit Union">Credit Union</option>
                  <option value="Crescent">Crescent</option>
                  <option value="Driveway">Driveway</option>
                  <option value="Exeter">Exeter</option>
                  <option value="First Help Financial">First Help Financial</option>
                  <option value="Foursight">Foursight</option>
                  <option value="Global">Global</option>
                  <option value="LendBuzz">LendBuzz</option>
                  <option value="Other">Other</option>
                  <option value="Prestige">Prestige</option>
                  <option value="Regional">Regional</option>
                  <option value="Santander">Santander</option>
                  <option value="Wells Fargo">Wells Fargo</option>
                  <option value="Westlake">Westlake</option>
                </select>
              </div>
            </>
          )}

          {feedbackMessage.message && (
            <div
              className={`feedback-message ${feedbackMessage.type}`}
              role="alert"
            >
              {feedbackMessage.message}
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Save Disposition'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CaptureDisposition;
